// /home/coder/project/frontend/src/pages/trade/ToContractsList.tsx

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Input, Select, Space, Row, Col, Typography, message, Tag, Tooltip } from 'antd';
import {  SearchOutlined,  PlusOutlined,  EyeOutlined,  EditOutlined,  FileTextOutlined,} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import StandardTable from '../../../components/common/StandardTable';
import { CgAttachment } from 'react-icons/cg';

// Import the specific interface for the view data
import { VwToContractDetails } from '../../../types';
import { useGetContractDetails } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

const { Title } = Typography;
const { Option } = Select;

 const formatNumberWithGrouping = (raw: string | number | null | undefined): string => {
   if (raw === null || raw === undefined) return '';
   if (typeof raw === 'number') {
     return new Intl.NumberFormat(undefined, { maximumFractionDigits: 6 }).format(raw);
   }

   const trimmed = String(raw).trim();
   if (!trimmed) return '';

   const normalized = trimmed.replace(/,/g, '');
   const numeric = Number(normalized);
   if (!Number.isFinite(numeric)) return trimmed;

   const decimalPart = normalized.split('.')[1];
   const fractionDigits = decimalPart ? Math.min(decimalPart.length, 6) : 0;

   return new Intl.NumberFormat(undefined, {
     minimumFractionDigits: fractionDigits,
     maximumFractionDigits: fractionDigits,
   }).format(numeric);
 };

// Wrapper for react-icons attachment icon (consistent with other list UIs)
const RCgAttachment: React.FC<any> = (props) =>
  React.createElement(CgAttachment as unknown as React.ComponentType<any>, props) as React.ReactElement;

// Interface for filters
interface Filters {
  search: string;
  company_name: string | null;
  contract_type: string | null;
  payment_type: string | null;
  status: string | null;
}

const ToContractsList: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filters>({
    search: '',
    company_name: null,
    contract_type: null,
    payment_type: null,
    status: null,
  });

  // --- Data Fetching ---
  const { data: contractDetails, isLoading, error } = useGetContractDetails();
  const contractList = contractDetails?.data ?? [];

  // --- Handle Loading & Error ---
  if (error) {
    message.error(`Error fetching contracts: ${error.message}`);
  }

  // --- Derive Unique Filter Options ---
  const uniqueCompanyNames = useMemo(() => {
    if (!contractList) return [];
    const names = contractList.map(c => c.company_name).filter((name): name is string => !!name);
    return Array.from(new Set(names));
  }, [contractList]);

  const uniqueContractTypes = useMemo(() => {
    if (!contractList) return [];
    const types = contractList.map(c => c.contract_type).filter((type): type is VwToContractDetails['contract_type'] => !!type);
    return Array.from(new Set(types));
  }, [contractList]);

  const uniquePaymentTypes = useMemo(() => {
    if (!contractList) return [];
    const types = contractList.map(c => c.payment_type).filter((type): type is VwToContractDetails['payment_type'] => !!type);
    return Array.from(new Set(types));
  }, [contractList]);

  const uniqueStatuses = useMemo(() => {
    if (!contractList) return [];
    const statuses = contractList.map(c => c.status).filter((status): status is VwToContractDetails['status'] => !!status);
    return Array.from(new Set(statuses));
  }, [contractList]);
  // ------------------------------------

  // Filter contracts based on the filters
  const filteredContracts = contractList.filter((contract) => {
    const searchTerm = filters.search.toLowerCase();
    const searchFields = [
      contract.id?.toString() || '',
      contract.contract_name?.toLowerCase() || '',
      contract.reference_id?.toLowerCase() || '',
      contract.company_name?.toLowerCase() || '', // Search company name from view
      contract.description?.toLowerCase() || '',
    ];

    if (searchTerm && !searchFields.some(field => field.includes(searchTerm))) {
      return false;
    }
    if (filters.company_name && contract.company_name !== filters.company_name) {
      return false;
    }
    if (filters.contract_type && contract.contract_type !== filters.contract_type) {
      return false;
    }
    if (filters.payment_type && contract.payment_type !== filters.payment_type) {
      return false;
    }
    if (filters.status && contract.status !== filters.status) {
      return false;
    }
    return true;
  });

  // --- Table Columns Definition ---
  const columns: ColumnsType<VwToContractDetails> = [
    {
      title: '',
      key: 'has_files',
      width: 40,
      align: 'center',
      render: (_: unknown, row: VwToContractDetails) => {
        const has = !!row.upload_path && String(row.upload_path).trim() !== '';
        return has ? (
          <Tooltip title="Has attachments">
            <RCgAttachment size={16} color="#1677ff" />
          </Tooltip>
        ) : null;
      },
    },
    {
      title: 'Contract Name',
      dataIndex: 'contract_name',
      key: 'contract_name',
      sorter: (a: VwToContractDetails, b: VwToContractDetails) => a.contract_name.localeCompare(b.contract_name),
      ellipsis: true,
    },
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company_name',
      sorter: (a: VwToContractDetails, b: VwToContractDetails) => a.company_name.localeCompare(b.company_name),
      ellipsis: true,
    },
    { title: 'Reference ID', dataIndex: 'reference_id', key: 'reference_id', width: 120,ellipsis: true },
    {
      title: 'Value 1',
      dataIndex: 'contract_value1',
      key: 'value1', // Combine value and currency for key
      render: (value: string, record: VwToContractDetails) => `${formatNumberWithGrouping(value)} ${record.contract_currency1}`,
      sorter: (a: VwToContractDetails, b: VwToContractDetails) => parseFloat(a.contract_value1) - parseFloat(b.contract_value1),
      align: 'right',
    },
    // Optional: Add Value 2/3 if needed
    // {
    //   title: 'Value 2',
    //   dataIndex: 'contract_value2',
    //   key: 'contract_value2',
    //   render: (value, record) => value ? `${value} ${record.contract_currency2}` : '-', // Simple display
    //   sorter: (a, b) => parseFloat(a.contract_value2 || '0') - parseFloat(b.contract_value2 || '0'),
    //   align: 'right',
    // },
    {
      title: 'Start',
      dataIndex: 'start_date',
      key: 'start_date',
        width: 90,
      render: (date: string | null) => (date ? new Date(date).toLocaleDateString() : '-'),
      sorter: (a: VwToContractDetails, b: VwToContractDetails) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime(),
    },
    {
      title: 'End',
      dataIndex: 'end_date',
      key: 'end_date',
        width: 90,
      render: (date: string | null) => (date ? new Date(date).toLocaleDateString() : '-'),
      sorter: (a: VwToContractDetails, b: VwToContractDetails) => new Date(a.end_date || 0).getTime() - new Date(b.end_date || 0).getTime(),
    },
    {
      title: 'Type',
      dataIndex: 'contract_type',
      key: 'contract_type',
        width: 80,
      render: (type: VwToContractDetails['contract_type']) => type,
      filters: uniqueContractTypes.map(t => ({ text: t, value: t })),
      onFilter: (value: unknown, record: VwToContractDetails) => record.contract_type === value,
    },
    {
      title: 'Payment',
      dataIndex: 'payment_type',
      key: 'payment_type',
        width:105,
      render: (type: VwToContractDetails['payment_type']) => type,
      filters: uniquePaymentTypes.map(t => ({ text: t, value: t })),
      onFilter: (value: unknown, record: VwToContractDetails) => record.payment_type === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
        width:100,
      render: (status: VwToContractDetails['status']) => {
        let color: string = 'default';
        if (status === 'active') color = 'green';
        else if (status === 'inactive') color = 'orange';
        else if (status === 'terminated') color = 'red';
        return <Tag color={color}>{status}</Tag>;
      },
      filters: uniqueStatuses.map(s => ({ text: s, value: s })),
      onFilter: (value: unknown, record: VwToContractDetails) => record.status === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 80,
      render: (_: unknown, record: VwToContractDetails) => (
        <Space>
          <Button action="read" icon={<EyeOutlined />} onClick={() => navigate(`/to/contracts/show/${record.id}`)} size="small" />
          <Button action="edit" type="primary" icon={<EditOutlined />} onClick={() => navigate(`/to/contracts/edit/${record.id}`)} size="small" />
        </Space>
      ),
    },
  ];

  // --- Render ---
  return (
    <Card
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: 8 }} /> Contracts
            </Title>
          </Col>
          <Col>
            <Button
              action="add"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/to/contracts/create')} // Navigate to create page
            >
              Create New Contract
            </Button>
          </Col>
        </Row>
      }
      bordered={false}
    >
      {/* Filter Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }} justify="start">
        <Col xs={24} sm={12} md={6} lg={5}>
          <Input
            placeholder="Search ID, Name, Ref, Company..."
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            allowClear
            disabled={isLoading}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Select
            placeholder="Filter by Company"
            value={filters.company_name}
            onChange={(value) => setFilters((prev) => ({ ...prev, company_name: value }))}
            allowClear showSearch
            // Explicitly cast option.children to string before using toLowerCase
            filterOption={(input, option) => String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
            style={{ width: '100%' }}
            disabled={isLoading || uniqueCompanyNames.length === 0}
          >
            {uniqueCompanyNames.map(name => <Option key={name} value={name}>{name}</Option>)}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Select
            placeholder="Filter by Contract Type"
            value={filters.contract_type}
            onChange={(value) => setFilters((prev) => ({ ...prev, contract_type: value }))}
            allowClear style={{ width: '100%' }}
            disabled={isLoading || uniqueContractTypes.length === 0}
          >
            {/* Display raw type in options */}
            {uniqueContractTypes.map(type => <Option key={type} value={type}>{type}</Option>)}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Select
            placeholder="Filter by Payment Type"
            value={filters.payment_type}
            onChange={(value) => setFilters((prev) => ({ ...prev, payment_type: value }))}
            allowClear style={{ width: '100%' }}
            disabled={isLoading || uniquePaymentTypes.length === 0}
          >
            {/* Display raw type in options */}
            {uniquePaymentTypes.map(type => <Option key={type} value={type}>{type}</Option>)}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Select
            placeholder="Filter by Status"
            value={filters.status}
            onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
            allowClear style={{ width: '100%' }}
            disabled={isLoading || uniqueStatuses.length === 0}
          >
            {/* Display raw status in options */}
            {uniqueStatuses.map(status => <Option key={status} value={status}>{status}</Option>)}
          </Select>
        </Col>
      </Row>

      {/* Data Table */}
      <StandardTable
        columns={columns}
        dataSource={filteredContracts}
        rowKey="id"
        size="small"
        loading={isLoading}
        enableExport={true}
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
};

export default ToContractsList;
